import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useDebounce from "~/shared/hooks/useDebounce";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  parseDate,
  toDateString,
  toSelectFilter,
} from "~/pages/Directory/shared/filterHelpers";
import { IPartnersUrlParams } from "~/pages/Partners/model/partnersUrlParams";
import { fetchGeoPlaces, GeoPlaceOption } from "~/shared/lib/addressGeo";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";

import { PARTNERS_STATUS_OPTIONS } from "../../../../MOCK/partners";

import s from "./PartnersFilter.less";

const toSelectOptions = (items: GeoPlaceOption[]) =>
  items.map((item) => ({ value: item.id, label: item.name }));

export interface IPartnersFilterForm {
  id?: string;
  name?: string;
  inn?: string;
  status?: { value: string | null; label: string } | null;
  createdFrom?: Date | null;
  createdTo?: Date | null;
  country?: { value: string | null; label: string } | null;
  fo?: { value: string | null; label: string } | null;
  region?: { value: string | null; label: string } | null;
  city?: { value: string | null; label: string } | null;
  author?: string;
}

export const EMPTY_PARTNERS_FILTER: IPartnersFilterForm = {
  id: undefined,
  name: undefined,
  inn: undefined,
  status: null,
  createdFrom: null,
  createdTo: null,
  country: null,
  fo: null,
  region: null,
  city: null,
  author: undefined,
};

const PartnersFilter: FC = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const {
    id,
    name,
    inn,
    status,
    createdFrom,
    createdTo,
    country,
    fo,
    region,
    city,
    author,
  } = useURLParamsGetter<IPartnersUrlParams>();

  const statusOptions = [
    { value: "all", label: t("partners.status.all") },
    ...PARTNERS_STATUS_OPTIONS.map((status) => ({
      value: status,
      label: status,
    })),
  ];
  const defaultStatusOption = statusOptions[1];

  const [countryOptions, setCountryOptions] = useState<GeoPlaceOption[]>([]);
  const [foOptions, setFoOptions] = useState<GeoPlaceOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<GeoPlaceOption[]>([]);
  const [cityOptions, setCityOptions] = useState<GeoPlaceOption[]>([]);
  const [citySearch, setCitySearch] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const cityPaginationRef = useRef({ page: 0, hasMore: false });
  const debouncedCitySearch = useDebounce(citySearch, 300);

  useEffect(() => {
    fetchGeoPlaces({ type: "country", count: 10000 })
      .then((result) => setCountryOptions(result.items))
      .catch(() => undefined);
    fetchGeoPlaces({ type: "FO", count: 100 })
      .then((result) => setFoOptions(result.items))
      .catch(() => undefined);
    fetchGeoPlaces({ type: "subject", count: 1000 })
      .then((result) => setRegionOptions(result.items))
      .catch(() => undefined);
  }, []);

  const loadCities = useCallback(
    (search: string, page: number, append: boolean) => {
      setCityLoading(true);
      fetchGeoPlaces({
        type: "city",
        ...(search ? { name: search } : {}),
        count: 100,
        ...(page > 1 ? { page } : {}),
      })
        .then((result) => {
          cityPaginationRef.current = {
            page: result.page,
            hasMore: result.hasMore,
          };
          setCityOptions((prev) =>
            append ? [...prev, ...result.items] : result.items,
          );
          setCityLoading(false);
        })
        .catch(() => setCityLoading(false));
    },
    [],
  );

  useEffect(() => {
    loadCities(debouncedCitySearch, 1, false);
  }, [debouncedCitySearch, loadCities]);

  const handleCityPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { page, hasMore } = cityPaginationRef.current;

    if (!hasMore || cityLoading) {
      return;
    }

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 32) {
      loadCities(debouncedCitySearch, page + 1, true);
    }
  };

  const resolveStatusDefault = () => {
    if (status === "all") return statusOptions[0];
    return toSelectFilter(status) ?? defaultStatusOption;
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      id: id || "",
      name: name || "",
      inn: inn || "",
      status: resolveStatusDefault(),
      createdFrom: parseDate(createdFrom),
      createdTo: parseDate(createdTo),
      country: toSelectFilter(country),
      fo: toSelectFilter(fo),
      region: toSelectFilter(region),
      city: toSelectFilter(city),
      author: author || "",
    },
  });

  const onSubmit = (data) => {
    setParams({
      id: data.id || undefined,
      name: data.name || undefined,
      inn: data.inn || undefined,
      status: data.status?.value || undefined,
      createdFrom: toDateString(data.createdFrom),
      createdTo: toDateString(data.createdTo),
      country: data.country?.value ? data.country.label : undefined,
      fo: data.fo?.value ? data.fo.label : undefined,
      region: data.region?.value ? data.region.label : undefined,
      city: data.city?.value ? data.city.label : undefined,
      author: data.author || undefined,
      page: "0",
    });
  };

  const cancelForm = () => {
    reset({
      id: "",
      name: "",
      inn: "",
      status: statusOptions[0],
      createdFrom: null,
      createdTo: null,
      country: null,
      fo: null,
      region: null,
      city: null,
      author: "",
    });
    setParams({
      id: undefined,
      name: undefined,
      inn: undefined,
      status: "all",
      createdFrom: undefined,
      createdTo: undefined,
      country: undefined,
      fo: undefined,
      region: undefined,
      city: undefined,
      author: undefined,
      page: "0",
    });
  };

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.name")}</span>
          <Controller
            name="name"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.inn")}</span>
          <Controller
            name="inn"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.status")}</span>
          <Controller
            name="status"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={statusOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.createdAt")}</span>
          <div className={s.rangeWrapp}>
            <Controller
              control={control}
              name="createdFrom"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("partners.filter.dateFrom")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
            <span className={s.rangeDivider}>-</span>
            <Controller
              control={control}
              name="createdTo"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("partners.filter.dateTo")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
          </div>
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.id")}</span>
          <Controller
            name="id"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
      </div>
      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.country")}</span>
          <Controller
            name="country"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                labelInValue
                placeholder={t("partners.filter.selectPlaceholder")}
                value={value}
                onChange={(option) => onChange(option ?? null)}
                options={toSelectOptions(countryOptions)}
                filterOption={filterOptionByLabel}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.fo")}</span>
          <Controller
            name="fo"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                labelInValue
                placeholder={t("partners.filter.selectPlaceholder")}
                value={value}
                onChange={(option) => onChange(option ?? null)}
                options={toSelectOptions(foOptions)}
                filterOption={filterOptionByLabel}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.region")}</span>
          <Controller
            name="region"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                labelInValue
                placeholder={t("partners.filter.selectPlaceholder")}
                value={value}
                onChange={(option) => onChange(option ?? null)}
                options={toSelectOptions(regionOptions)}
                filterOption={filterOptionByLabel}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.city")}</span>
          <Controller
            name="city"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                labelInValue
                placeholder={t("partners.filter.selectPlaceholder")}
                value={value}
                onChange={(option) => {
                  setCitySearch("");
                  onChange(option ?? null);
                }}
                options={toSelectOptions(cityOptions)}
                filterOption={false}
                onSearch={setCitySearch}
                onPopupScroll={handleCityPopupScroll}
                loading={cityLoading}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("partners.filter.author")}</span>
          <Controller
            name="author"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
      </div>
      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={cancelForm}
        >
          {t("partners.reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("partners.show")}
        </Button>
      </div>
    </form>
  );
};

export default PartnersFilter;
